/* eslint-disable no-restricted-globals, no-restricted-syntax -- Native tools use the modern PWA browser baseline. */
/** Public set possibilities, never a prediction of an opponent's actual set. */
import { toID } from './battle-dex';

export interface BattleToolRole {
	abilities: string[];
	items: string[];
	teraTypes: string[];
	moves: string[];
	evs?: { [stat: string]: number };
	ivs?: { [stat: string]: number };
}
export interface BattleToolSet {
	level: number;
	roles: { [name: string]: BattleToolRole };
	evs?: { [stat: string]: number };
	ivs?: { [stat: string]: number };
}
interface BattleToolOfficialSet {
	level: number;
	sets: { role: string, abilities: string[], teraTypes: string[], movepool: string[] }[];
}
export class BattleToolsData {
	static sets: { [id: string]: BattleToolSet } = {};
	static checkedAt = '';
	static status = 'Loading current random-battle sets…';
	static pending: Promise<void> | null = null;
	static lastAttempt = 0;
	static stringList(value: unknown): value is string[] {
		return Array.isArray(value) && value.length <= 100 &&
			value.every(entry => typeof entry === 'string' && entry.length < 100);
	}
	static validateSets(value: unknown): value is { [name: string]: BattleToolSet } {
		if (!value || typeof value !== 'object' || Object.keys(value).length < 400) return false;
		return Object.values(value).every((set: BattleToolSet) => set && set.level >= 1 && set.level <= 100 &&
			set.roles && Object.values(set.roles).every(role => role &&
				['abilities', 'items', 'teraTypes', 'moves'].every(key =>
					(key === 'items' && role.items === undefined) || this.stringList(role[key as keyof BattleToolRole]))));
	}
	static validateOfficial(value: unknown): value is { [id: string]: BattleToolOfficialSet } {
		if (!value || typeof value !== 'object' || Object.keys(value).length < 400) return false;
		return Object.values(value).every((set: BattleToolOfficialSet) => set && set.level >= 1 && set.level <= 100 &&
			Array.isArray(set.sets) && set.sets.length > 0 && set.sets.every(role => role &&
				typeof role.role === 'string' && this.stringList(role.movepool) &&
				this.stringList(role.abilities) && this.stringList(role.teraTypes)));
	}
	static reconcile(sets: { [name: string]: BattleToolSet }, official: { [id: string]: BattleToolOfficialSet }) {
		const byID: { [id: string]: BattleToolSet } = {};
		for (const name in sets) byID[toID(name)] = sets[name];
		let mismatches = 0;
		const result: typeof byID = {};
		Object.keys(official).forEach(id => {
			const source = official[id];
			const sampled = byID[id];
			const roles: BattleToolSet['roles'] = {};
			const counts: { [name: string]: number } = {};
			let speciesMatches = !!sampled && sampled.level === source.level;
			source.sets.forEach(role => {
				const sample = sampled?.roles[role.role];
				const same = (a: string[], b: string[]) => a.map(toID).sort().join(',') === b.map(toID).sort().join(',');
				const variants = source.sets.filter(entry => entry.role === role.role);
				const union = (key: 'movepool' | 'abilities' | 'teraTypes') =>
					Array.from(new Set(variants.reduce<string[]>((all, entry) => all.concat(entry[key]), [])));
				const matches = sample && source.level === sampled.level && same(sample.moves, union('movepool')) &&
					same(sample.abilities, union('abilities')) && same(sample.teraTypes, union('teraTypes'));
				if (!matches) { mismatches++; speciesMatches = false; }
				counts[role.role] = (counts[role.role] || 0) + 1;
				const label = variants.length > 1 ? `${role.role} · ${counts[role.role]}` : role.role;
				roles[label] = {
					moves: role.movepool, abilities: role.abilities, teraTypes: role.teraTypes,
					items: matches ? sample.items || [] : [], evs: matches ? sample.evs : undefined,
					ivs: matches ? sample.ivs : undefined,
				};
			});
			result[id] = { level: source.level, roles,
				evs: speciesMatches ? sampled.evs : undefined, ivs: speciesMatches ? sampled.ivs : undefined };
		});
		this.sets = result;
		return mismatches;
	}
	static async fetchJSON(url: string): Promise<unknown> {
		const controller = new AbortController();
		const timer = setTimeout(() => controller.abort(), 10000);
		try {
			const response = await fetch(url, { signal: controller.signal, cache: 'no-cache', credentials: 'omit' });
			if (!response.ok) throw new Error('Set data unavailable');
			return await response.json();
		} finally {
			clearTimeout(timer);
		}
	}
	static load(force = false): Promise<void> {
		if (this.pending) return this.pending;
		if (!force && Date.now() - this.lastAttempt < 3600000) return Promise.resolve();
		this.lastAttempt = Date.now();
		this.pending = this.refresh().then(() => { this.pending = null; });
		return this.pending;
	}
	static async refresh() {
		try {
			const [sets, official] = await Promise.all([
				this.fetchJSON('https://pkmn.github.io/randbats/data/gen9randombattle.json').catch(() => null),
				this.fetchJSON('https://raw.githubusercontent.com/smogon/pokemon-showdown/master/data/random-battles/gen9/sets.json'),
			]);
			if (!this.validateOfficial(official)) throw new Error('Invalid set data');
			const differences = this.reconcile(this.validateSets(sets) ? sets : {}, official);
			this.checkedAt = new Date().toISOString();
			this.status = differences ? 'Live Showdown pools • item feed differs; affected items are unknown' :
				'Live sets • checked against Showdown';
		} catch {
			if (Object.keys(this.sets).length) {
				this.status = 'Refresh unavailable • using previously checked sets';
				return;
			}
			try {
				const fallback = await this.fetchJSON('/battle-data/gen9.json') as {
					schemaVersion: number, checkedAt: string, sets: unknown, official: unknown,
				};
				if (fallback?.schemaVersion !== 1 || !Number.isFinite(Date.parse(fallback.checkedAt)) ||
					!this.validateSets(fallback.sets) || !this.validateOfficial(fallback.official)) throw new Error('Invalid fallback');
				this.reconcile(fallback.sets, fallback.official);
				this.checkedAt = fallback.checkedAt;
				this.status = 'Live refresh unavailable • bundled fallback';
			} catch {
				this.status = 'Set data unavailable • use revealed moves and manual assumptions';
			}
		}
	}
}
